import { useState, useRef, useCallback, useEffect, type ReactNode } from "react";
import { highlightCode } from "@/lib/highlighter";
import { useChatStore } from "@/stores/chatStore";

interface CodeBlockCopyProps {
  children: ReactNode;
  language?: string;
}

const LANGUAGE_LABELS: Record<string, string> = {
  js: "JavaScript",
  javascript: "JavaScript",
  jsx: "JSX",
  ts: "TypeScript",
  typescript: "TypeScript",
  tsx: "TSX",
  json: "JSON",
  sql: "SQL",
  py: "Python",
  python: "Python",
  bash: "Bash",
  sh: "Shell",
  shell: "Shell",
  html: "HTML",
  css: "CSS",
  xml: "XML",
  yaml: "YAML",
  yml: "YAML",
  markdown: "Markdown",
  md: "Markdown",
  graphql: "GraphQL",
};

// Normalize language aliases to shiki-compatible ids
const LANG_ALIASES: Record<string, string> = {
  js: "javascript",
  ts: "typescript",
  py: "python",
  sh: "bash",
  shell: "bash",
  yml: "yaml",
  md: "markdown",
};

function extractText(node: ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (!node) return "";
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (typeof node === "object" && "props" in node) {
    return extractText((node as { props: { children?: ReactNode } }).props.children);
  }
  return "";
}

function useTheme(): string {
  const [theme, setTheme] = useState(() =>
    document.documentElement.getAttribute("data-theme") ?? "light"
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.documentElement.getAttribute("data-theme") ?? "light");
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);
  return theme;
}

export function CodeBlockCopy({ children, language }: CodeBlockCopyProps) {
  const [copied, setCopied] = useState(false);
  const [highlightedHtml, setHighlightedHtml] = useState<string | null>(null);
  const plainRef = useRef<HTMLPreElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const codeText = extractText(children);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const theme = useTheme();

  const handleCopy = useCallback(() => {
    const text = highlightRef.current?.textContent ?? plainRef.current?.textContent ?? codeText;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [codeText]);

  // Highlight with Shiki when language is known and not streaming
  // Re-runs when theme changes to update syntax colors
  useEffect(() => {
    if (!language || !codeText || isStreaming) {
      if (isStreaming) setHighlightedHtml(null);
      return;
    }
    const langId = LANG_ALIASES[language.toLowerCase()] ?? language.toLowerCase();
    const isDark = theme === "dark";
    let cancelled = false;
    highlightCode(codeText, langId, isDark ? "github-dark" : "github-light").then((html) => {
      if (!cancelled) setHighlightedHtml(html);
    });
    return () => { cancelled = true; };
  }, [language, codeText, isStreaming, theme]);

  const label = language
    ? LANGUAGE_LABELS[language.toLowerCase()] ?? language.toUpperCase()
    : null;

  return (
    <div className="cx-code-block">
      {label && <span className="cx-code-lang">{label}</span>}
      <button
        type="button"
        onClick={handleCopy}
        className="cx-code-copy cx-msg-action"
        aria-label="Copy code"
        data-tooltip={copied ? "Copied" : "Copy"}
      >
        {copied ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        )}
      </button>
      {highlightedHtml ? (
        <div
          ref={highlightRef}
          className="cx-code-highlighted"
          dangerouslySetInnerHTML={{ __html: highlightedHtml }}
        />
      ) : (
        <pre ref={plainRef}>{children}</pre>
      )}
    </div>
  );
}
